import { DashboardWidget as DashboardWidget, ObsoleteAdAccount } from '../types/dashboard.types';
import _, { keyBy } from 'lodash';
import * as R from 'ramda';

export const CONNECTION_TYPES = {
  PIXEL: 'facebookPixel',
  GA: 'googleAnalytics',
  FB_PAGE: 'facebookPage',
  FB_APP: 'fbApp',
  INSTAGRAM_PAGE: 'instagram',
  YELP_ACCOUNT: 'yelpAccount',
  YOUTUBE_CHANNEL: 'youtubeChannel',
  GOOGLE_AD_ACCOUNT: 'googleAdAccount',
  FACEBOOK_AD_ACCOUNT: 'facebookAdAccount',
  MICROSOFT_AD_ACCOUNT: 'microsoftAdAccount',
  NAVER_AD_ACCOUNT: 'naverAdAccount',
  KAKAO_AD_ACCOUNT: 'kakaoAdAccount',
  AMAZON_AD_ACCOUNT: 'amazonAdAccount',
  LINKEDIN_AD_ACCOUNT: 'linkedinAdAccount',
  TIKTOK_AD_ACCOUNT: 'tiktokAdAccount',
  TWITTER_AD_ACCOUNT: 'twitterAdAccount',
  SNAPCHAT_AD_ACCOUNT: 'snapchatAdAccount',
  CRITEO_AD_ACCOUNT: 'criteoAdAccount',
  APPSFLYER_APP: 'appsFlyer',
  YAHOO_AD_ACCOUNT: 'yahooAdAccount',
  APPLE_AD_ACCOUNT: 'appleAdAccount',
  BRANCH_APP: 'branch',
  PINTEREST_AD_ACCOUNT: 'pinterestAdAccount',
  GOOGLE_SHEETS: 'googleSpreadSheet',
  APPSTORECONNECT_ACCOUNT: 'appStoreConnectAccount',
  PLAYSTORE_ACCOUNT: 'playStoreAccount',
  REDDIT_AD_ACCOUNT: 'redditAdAccount',
  NAVER_GFA: 'naverGFAAdAccount',
  MAILCHIMP_AD_ACCOUNT: 'mailchimpAdAccount',
  ADVERITY: 'adverity',
  GA4: 'googleAnalytics4',
  INTERNAL: 'internal',
  AIRBYTE: 'airbyte',
};

const ADS_EXCLUDED_CHANNELS = new Set([
  CONNECTION_TYPES.MAILCHIMP_AD_ACCOUNT,
  CONNECTION_TYPES.GOOGLE_SHEETS,
  'adverity',
  'instagramPublic',
  'youtubePublic',
  'tiktokPublic',
  'airtable',
  'mysql',
  'mailchimp',
]);
const ECOMMERCE_CHANNELS = new Set(['coupang', 'cafe24', 'naverCommerce', 'shopify', 'godomall']);

export const GA_TYPES = new Set([CONNECTION_TYPES.GA, CONNECTION_TYPES.GA4]);
export const checkIsGaSource = ({ channel = '' }) => GA_TYPES.has(channel);
export const checkIsEcommerce = ({ channel = '' }) => ECOMMERCE_CHANNELS.has(channel);
export const checkIsAnalyticTool = R.prop('is_analytic');

// Old function
// export const checkIsAnalyticSource = R.anyPass([
//   R.prop('is_analytic'),
//   R.prop('isCustomReportAccount'),
// ]);

export const checkIsAnalyticSource = R.compose(
  R.anyPass([R.prop('is_analytic'), R.prop('isCustomReportAccount')]),
  R.defaultTo({}),
);

export const isCustomTableSource = (source: any = {}) =>
  checkIsAnalyticSource(source) && !checkIsGaSource(source) && !checkIsEcommerce(source) && !checkIsAnalyticTool(source);

export const isGraphUsableBlendSetting = R.compose(
  R.allPass([R.propSatisfies(Boolean, 'dateFormat'), R.propSatisfies(Boolean, 'dateField')]),
  R.defaultTo({}),
);

export const isGraphSource = (source: Record<string, any> = {}) => {
  const { channel, settings, status } = source;
  switch (channel) {
    case 'adverity':
    case 'airbyte':
      return status === 'ACTIVE' && isGraphUsableBlendSetting(settings || {});
    case 'googleSpreadSheet':
      return isGraphUsableBlendSetting(settings || {});
    default: {
      return !isCustomTableSource(source);
    }
  }
};

export function selectDataSources(allAdAccounts: ObsoleteAdAccount[], widget: DashboardWidget): ObsoleteAdAccount[] {
  if ('adAccounts' in widget) {
    return _.intersectionBy(allAdAccounts, widget.adAccounts, 'channelId');
  }

  // if ("adAccounts" in widget) {
  //   // allAdAccount becomes the intersection between allAdAccounts and widget.adAccounts
  //   let widgetAdAccountsByChannelAndId = keyBy(widget.adAccounts, aa => aa.channel + "-" + aa.channelId)
  //   allAdAccounts = allAdAccounts.filter(aa => widgetAdAccountsByChannelAndId[aa.channel + "-" + aa.channelId] != undefined)
  // }

  if (widget.type == 'pivotTable') {
    if (widget.subType == 'analytic') {
      //@ts-ignore
      return allAdAccounts.filter(aa => checkIsAnalyticSource(aa));
    } else {
      return allAdAccounts.filter(aa => !isCustomTableSource(aa));
    }
  }
  if (
    widget.type == 'graph' ||
    widget.type == 'singleMetric' ||
    widget.type == 'graphBoxes' // added this since "graphBoxes" had AdAccount issues. This seemed to fix the issue.
  ) {
    return allAdAccounts.filter(aa => isGraphSource(aa));
  }
  // if (widget.type == "pivotTable" || widget.type == "smallTable") {
  return allAdAccounts.filter(aa => !isCustomTableSource(aa));
  // }
}

// For isCustomTable below

export const BLEND_CHANNELS = new Set([
  // 'adverity', for filtering ACTIVE status
  'googleSpreadSheet',
  'csvWithDate',
  // 'mailchimpAdAccount',
  // 'appsFlyer',
]);

const isValueExist = R.both(R.complement(R.isNil), R.complement(R.isEmpty));

export const isBlendableSource = ({ channel, status }: Record<string, any> = {}) =>
  BLEND_CHANNELS.has(channel) || ((channel === 'adverity' || channel === 'airbyte') && status === 'ACTIVE');

/**
 * Check if table is CustomTable
 */
export function checkSourceTableMode(sources = []) {
  const sourceMap = sources.reduce(
    (acc, source) => {
      if (checkIsGaSource(source)) {
        acc.gas.push(source);
      } else if (isBlendableSource(source) && hasValidBlendSetting(source.settings)) {
        acc.blended.push(source);
      } else if (!isCustomTableSource(source)) {
        acc.marketing.push(source);
      } else if (checkIsAnalyticSource(source)) {
        acc.customs.push(source);
      }

      return acc;
    },
    {
      marketing: [],
      gas: [],
      blended: [],
      customs: [],
    },
  );

  const { marketing, gas, customs, blended } = sourceMap;

  const isAllBlended = sources.length === blended.length;

  if (
    !isValueExist(marketing) &&
    (isValueExist(gas) || isValueExist(customs) || (isValueExist(blended) && !isAllBlended))
  ) {
    return 'analytic';
  } else {
    return 'ads';
  }
}

const hasValidBlendSetting = (settings: any) => {
  if (!isValueExist(settings)) return false;

  const { blendDataByBreakdown = {}, headers = [], dateField } = settings;

  const hasBreakdownValue = R.compose(
    //@ts-ignore
    R.any(R.prop('key')),
    R.values,
  )(blendDataByBreakdown);

  const hasHeaders = R.any(isValueExist, headers);

  return hasBreakdownValue || hasHeaders || dateField;
};

//  dimensionID
// @ts-ignore
const indexById = R.indexBy(R.prop('id'));
const DEFAULT_BREAKDOWN = 'channel';
const WORD_CLOUD_DIMENSION_ID = 'creativeWord';
const COLOR_CLUSTERING_DIMENSION_ID = 'colorClustering';
const EMOJI_ANALYSIS_DIMENSION_ID = 'emojiAnalysis';
const FUNNEL_DIMENSION_ID = 'all';

const DIMENSION_ID_MAP = {
  WORD_CLOUD: WORD_CLOUD_DIMENSION_ID,
  EMOJI_ANALYSIS: EMOJI_ANALYSIS_DIMENSION_ID,
  COLOR_CLUSTERING: COLOR_CLUSTERING_DIMENSION_ID,
  FUNNEL: FUNNEL_DIMENSION_ID,

  wordCloud: WORD_CLOUD_DIMENSION_ID,
  emoji_analysis: EMOJI_ANALYSIS_DIMENSION_ID,
  color_clustering: COLOR_CLUSTERING_DIMENSION_ID,
  funnel: FUNNEL_DIMENSION_ID,
};

export function selectedDimensionId(widget, selectedGraphType, dimensions, useComparisonDate?: boolean) {
  if (useComparisonDate) {
    return 'all';
  }

  const fixedDimensionId = DIMENSION_ID_MAP[selectedGraphType];

  if (fixedDimensionId) {
    return fixedDimensionId;
  } else {
    const dimensionId = R.compose(R.when(isValueExist, String), R.head, R.propOr([], 'dimensionIds'))(widget);

    const isCustomDimensionId = R.compose(R.not, isNaN, Number)(dimensionId);

    if (isCustomDimensionId) {
      // @ts-ignore
      const dimensionMap = indexById(dimensions);
      const found = dimensionMap[dimensionId];
      return found ? found.id : DEFAULT_BREAKDOWN;
    } else {
      return dimensionId === 'keyword' || !dimensionId ? DEFAULT_BREAKDOWN : dimensionId;
    }
  }
}

export const CHART_TYPES = Object.freeze({
  BAR: 'bar',
  HORIZONTAL_BAR: 'horizontal_bar',
  LINE: 'line',
  COMBO_CHART: 'combo_chart',
  SCATTER: 'scatter',
  PIE: 'pie',
  WORD_CLOUD: 'wordCloud',
  COLOR_CLUSTERING: 'color_clustering',
  EMOJI_ANALYSIS: 'emoji_analysis',
  FUNNEL: 'funnel',
  DONUT: 'donut',
});
// if graph is one of these types, then do not add the sorting and others
export const CHART_TYPES_FOR_GET_TABLE = [
  CHART_TYPES.WORD_CLOUD,
  CHART_TYPES.COLOR_CLUSTERING,
  CHART_TYPES.EMOJI_ANALYSIS,
  CHART_TYPES.SCATTER,
  // CHART_TYPES.PIE, // remove old logic, make it similar to new Donut widget logic
  CHART_TYPES.FUNNEL,
  CHART_TYPES.COMBO_CHART,
] as Readonly<string>[];
